import type * as Sentry from '@sentry/core';
import type { ErrorEvent, EventHint } from '@sentry/core';

/**
 * Shape that shared code in this monorepo relies on for error/observability
 * reporting. Lifted directly from `@sentry/core`'s runtime function types so
 * any Sentry SDK instance is assignable without adapters.
 */
export interface Reporter {
  captureException: typeof Sentry.captureException;
  captureMessage: typeof Sentry.captureMessage;
  setUser: typeof Sentry.setUser;
  setExtra: typeof Sentry.setExtra;
  isInitialized: typeof Sentry.isInitialized;
  flush: typeof Sentry.flush;
}

/**
 * Cross-bundle storage for the registered reporter.
 *
 * Module-level `let` would be tempting, but Next.js can compile the same
 * source file into multiple chunks, and at runtime each chunk holds its own
 * module instance with its own copy of every `let`. A `setReporter` call from
 * the instrumentation bundle would write to a different slot than a
 * `getReporter` call from a route-handler bundle, so the route would see a
 * null reporter even though registration "happened."
 *
 * `globalThis` is per-realm (one per Lambda / one per browser tab), so every
 * module instance — regardless of bundling — sees the same property. This
 * is the same pattern Sentry's own SDK uses for `__SENTRY__` to survive
 * duplicate installs.
 *
 * `Symbol.for(...)` keys are realm-shared, so different `@bridge/utils/sentry`
 * instances agree on the slot without coordinating on a string name that
 * could collide with unrelated code.
 */
const REPORTER_SLOT = Symbol.for('@bridge/utils/sentry.reporter');

interface ReporterCarrier {
  [REPORTER_SLOT]?: Reporter;
}

function carrier(): ReporterCarrier {
  return globalThis as unknown as ReporterCarrier;
}

/**
 * Register the app's reporter instance. Call once at startup, before any
 * shared code runs — typically in Next.js instrumentation files.
 */
export function setReporter(reporter: Reporter): void {
  carrier()[REPORTER_SLOT] = reporter;
}

/**
 * Retrieve the registered reporter. Throws if nothing is registered — the
 * bootstrap is expected to run before any consumer calls reporter methods,
 * so an unset reporter indicates a startup bug we want to surface loudly.
 */
export function getReporter(): Reporter {
  const reporter = carrier()[REPORTER_SLOT];
  if (!reporter) {
    throw new Error(
      '[@bridge/utils/sentry] No reporter registered. Call setReporter() at app startup (typically in instrumentation-client.ts).',
    );
  }
  return reporter;
}

/**
 * Console-logging stand-in for the Reporter interface. Used in development so
 * engineers see what would be reported in the terminal without shipping events
 * to Sentry.
 */
export const mockReporter: Reporter = {
  captureException: (...args) => {
    console.log('[Sentry]:', ...args);
    return '';
  },
  captureMessage: (...args) => {
    console.log('[Sentry]:', ...args);
    return '';
  },
  setUser: (user) => {
    console.log('[Sentry]: Setting user', user);
  },
  setExtra: (key, value) => {
    console.log('[Sentry]: Setting extra', key, value);
  },
  isInitialized: () => true,
  flush: () => Promise.resolve(true),
};

/**
 * Standard reporter registration for Bridge Next.js apps. In development,
 * registers a console-mock reporter; otherwise registers the provided Sentry
 * SDK instance. Call once per Sentry runtime (client / server / edge) at
 * startup, before `Sentry.init`.
 */
export function registerReporter(sentry: Reporter): void {
  setReporter(process.env.NODE_ENV === 'development' ? mockReporter : sentry);
}

/**
 * `beforeSend`-compatible filter. Returns the event to forward it on, or
 * `null` to drop it.
 *
 * `hint` carries the value that was actually thrown (`originalException`),
 * which the serialized `event` no longer exposes — the only way a filter can
 * branch on structured fields like an HTTP `status` rather than message text.
 */
export type BeforeSendFilter = (event: ErrorEvent, hint?: EventHint) => ErrorEvent | null;

/**
 * Compose multiple filters into a single `beforeSend` handler. Filters run in
 * order; the first to return `null` short-circuits and drops the event.
 */
export function composeBeforeSend(...filters: BeforeSendFilter[]): BeforeSendFilter {
  return (event, hint) => {
    for (const filter of filters) {
      const result = filter(event, hint);
      if (result === null) {
        return null;
      }
    }
    return event;
  };
}

/**
 * Drops events caused by browser extensions (MetaMask, content scripts,
 * extension shims). These fire on any web app regardless of business logic
 * and are not actionable.
 */
export const filterBrowserExtensionErrors: BeforeSendFilter = (event) => {
  const isExtensionError =
    event.exception?.values?.some((exception) =>
      exception.stacktrace?.frames?.some(
        (frame) =>
          frame.filename?.includes('inpage.js') ||
          frame.filename?.includes('content-script') ||
          frame.filename?.includes('extension') ||
          frame.filename?.includes('chrome-extension') ||
          frame.filename?.includes('moz-extension'),
      ),
    ) ||
    event.message?.includes("Cannot read properties of null (reading 'type')") ||
    event.message?.includes('inpage.js') ||
    event.message?.includes('Failed to connect to MetaMask') ||
    event.message?.includes('MetaMask extension not found') ||
    event.exception?.values?.some(
      (exception) =>
        exception.value?.includes('Failed to connect to MetaMask') ||
        exception.value?.includes('MetaMask extension not found'),
    );

  if (isExtensionError) {
    console.warn(
      'Filtered browser extension error from Sentry:',
      event.message || event.exception?.values?.[0]?.value,
    );
    return null;
  }

  return event;
};

/**
 * Drops generic network errors (`Failed to fetch`, `Load failed`) that are
 * almost always transient and not actionable.
 */
export const filterNetworkErrors: BeforeSendFilter = (event) => {
  const isNetworkError =
    event.message?.includes('Load failed') ||
    event.message?.includes('Failed to fetch') ||
    event.exception?.values?.some(
      (exception) =>
        exception.type === 'TypeError' &&
        (exception.value?.includes('Load failed') || exception.value?.includes('Failed to fetch')),
    );

  if (isNetworkError) {
    console.warn(
      'Filtered network error from Sentry:',
      event.message || event.exception?.values?.[0]?.value,
    );
    return null;
  }

  return event;
};

/**
 * Drops the generic "An error occurred in the Server Components render"
 * wrapper events. The real underlying error is captured server-side via
 * `Sentry.captureRequestError` / `onRequestError`, so the client wrapper is
 * just noise.
 */
export const filterGenericRscErrors: BeforeSendFilter = (event) => {
  const isGenericRscError = event.exception?.values?.some((exception) =>
    exception.value?.includes('An error occurred in the Server Components render'),
  );

  if (isGenericRscError) {
    return null;
  }

  return event;
};

/**
 * Drops expired/invalid-session 401s. A dead session cookie is a normal logout
 * path — the app signs the user out and redirects — so there is nothing to fix
 * in the code, and one expiry fans out into an event per in-flight request.
 *
 * Only genuinely-expected dead sessions are dropped: a 401 carrying Stytch's
 * `session_not_found` error type. Other auth failures keep their own code, and a
 * bare 401 with no session marker is forwarded — it points at something wrong
 * with how the request was authorized rather than a session that simply aged out.
 *
 * The `Bad Session Token` message check is transitional: until the API change
 * that passes Stytch's error type through is deployed, expired sessions still
 * arrive with `code: 'not_allowed'` and only that message to go on. It is not
 * status-gated because throw sites can lose the status but keep the message.
 */
export const filterUnauthorizedErrors: BeforeSendFilter = (event, hint) => {
  const thrown: unknown = hint?.originalException;
  const isObject = typeof thrown === 'object' && thrown !== null;
  const code = isObject && 'code' in thrown ? thrown.code : undefined;
  const status = isObject && 'status' in thrown ? thrown.status : undefined;
  const isExpiredSession =
    (status === 401 && code === 'session_not_found') ||
    event.exception?.values?.some((exception) => exception.value?.includes('Bad Session Token'));

  if (isExpiredSession) {
    return null;
  }

  return event;
};

/**
 * Drop-in replacement for Sentry's top-level runtime API. Each method
 * delegates to the registered reporter, so the app's Sentry SDK configuration
 * (DSN, environment, sample rates, etc.) is in effect.
 *
 * Rest-arg forwarding preserves the caller's actual argument count — calling
 * with one arg forwards one arg, not `(arg, undefined)`.
 */
const reporter: Reporter = {
  captureException: (...args) => getReporter().captureException(...args),
  captureMessage: (...args) => getReporter().captureMessage(...args),
  setUser: (...args) => getReporter().setUser(...args),
  setExtra: (...args) => getReporter().setExtra(...args),
  isInitialized: (...args) => getReporter().isInitialized(...args),
  flush: (...args) => getReporter().flush(...args),
};

export default reporter;
